import React, { useState, useEffect, useCallback } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, Tags, Search } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import SportCategoryChip from "components/SportCategoryChip";
import { useAuth } from "contexts/AuthContext";
import { useDialog } from "contexts/DialogContext";
import { useI18n } from "i18n";
import api from "lib/api";

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-ring focus-visible:ring-offset-2";

/**
 * Categorias por modalidade (Sub-15 Masculino, Adulto Feminino…). Cada uma
 * carrega as cores do chip que aparece no card do evento. O filtro de
 * modalidade fica na URL (`?sport=<id>`) para o atalho da lista de modalidades
 * cair aqui ja filtrado.
 */
export default function SportCategories() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { confirm, alert } = useDialog();
  const { t, tf } = useI18n();
  const [items, setItems] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const sport = searchParams.get("sport") || "";
  const canManage = !!user?.capabilities?.manage_events;

  useEffect(() => {
    if (!canManage) return;
    api
      .get("/api/sports/")
      .then((res) => setSports(res.data || []))
      .catch(() => setSports([]));
  }, [canManage]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/sport-categories/", {
        params: { search: search || undefined, sport: sport || undefined },
      });
      setItems(res.data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [search, sport]);

  useEffect(() => {
    if (!canManage) return undefined;
    const timer = setTimeout(fetchItems, 300);
    return () => clearTimeout(timer);
  }, [fetchItems, canManage]);

  if (user && !canManage) {
    return <Navigate to="/app/dashboard" replace />;
  }

  const setSport = (value) =>
    setSearchParams(value ? { sport: value } : {}, { replace: true });

  const handleDelete = async (item) => {
    let failed = false;
    const confirmed = await confirm({
      title: t("sportCategory.deleteTitle"),
      description: tf("sportCategory.deleteBody", { name: item.name }),
      variant: "danger",
      confirmLabel: t("common.delete"),
      cancelLabel: t("common.cancel"),
      onConfirm: async () => {
        try {
          await api.delete(`/api/sport-categories/${item.id}/`);
        } catch (err) {
          failed = true;
          throw err;
        }
      },
    });

    if (confirmed) {
      fetchItems();
      return;
    }

    if (failed) {
      await alert({
        title: t("common.deleteError"),
        description: t("error.generic"),
        variant: "danger",
      });
    }
  };

  return (
    <div className="w-full animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {t("sportCategory.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("sportCategory.subtitle")}
          </p>
        </div>
        <Button
          onClick={() =>
            navigate("/app/sport-categories/new", { state: { sport } })
          }
        >
          <Plus className="h-4 w-4 mr-2" />
          {t("sportCategory.new")}
        </Button>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_280px]">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("sportCategory.searchPlaceholder")}
            className="pl-9"
          />
        </div>
        <select
          value={sport}
          onChange={(e) => setSport(e.target.value)}
          className={SELECT_CLASS}
          aria-label={t("event.sport")}
        >
          <option value="">{t("sportCategory.allSports")}</option>
          {sports.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </div>

      <Card className="w-full">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <Tags className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">
                {t("sportCategory.empty")}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("sportCategory.name")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("event.sport")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("sportCategory.chip")}
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      {t("sport.events")}
                    </th>
                    <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      onClick={() =>
                        navigate(`/app/sport-categories/${item.id}/edit`)
                      }
                      className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3 font-medium">{item.name}</td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {item.sport_name}
                      </td>
                      <td className="px-4 py-3">
                        <SportCategoryChip chip={item} />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {item.event_count}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() =>
                              navigate(`/app/sport-categories/${item.id}/edit`)
                            }
                            className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                            title={t("common.edit")}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(item)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-muted-foreground hover:text-red-600"
                            title={t("common.delete")}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
